<template>
  <WelcomeHeader />
  <GuidelineNav current="bangla" />

  <!-- ═══ HERO ═══ -->
  <section class="hero-section">
    <div class="hero-noise"></div>
    <div class="container hero-inner">
      <div class="hero-badge">বিসিএস প্রস্তুতি গাইড · সংস্করণ ৪.০</div>
      <h1 class="hero-title">বিসিএস বাংলা <span class="hero-accent">গাইডলাইন</span></h1>
      <p class="hero-sub">বিসিএস ৩৫ থেকে ৫০ পর্যন্ত সম্পূর্ণ টপিকভিত্তিক প্রশ্ন বিশ্লেষণ</p>

      <div class="hero-stats">
        <div class="hstat">
          <div class="hstat-num">৩০</div>
          <div class="hstat-lbl">মোট মার্ক</div>
        </div>
        <div class="hstat-sep"></div>
        <div class="hstat">
          <div class="hstat-num">১৫</div>
          <div class="hstat-lbl">বাংলা ভাষা</div>
        </div>
        <div class="hstat-sep"></div>
        <div class="hstat">
          <div class="hstat-num">১৫</div>
          <div class="hstat-lbl">বাংলা সাহিত্য</div>
        </div>
        <div class="hstat-sep"></div>
        <div class="hstat">
          <div class="hstat-num">১৬টি</div>
          <div class="hstat-lbl">বিসিএস বিশ্লেষণ</div>
        </div>
        <div class="hstat-sep"></div>
        <div class="hstat">
          <div class="hstat-num">৯ লাখ+</div>
          <div class="hstat-lbl">অ্যাপ ডাউনলোড</div>
        </div>
      </div>
    </div>
  </section>

  <!-- ═══ LEGEND BAR ═══ -->
  <div class="legend-bar">
    <div class="legend-inner">
      <span class="legend-label">হিটম্যাপ স্কেল:</span>
      <span class="lc heat-none">০</span>
      <span class="lc heat-low">১</span>
      <span class="lc heat-mid">২</span>
      <span class="lc heat-high">৩+</span>
      <span class="legend-sep">|</span>
      <span class="pri-tag p3">★★★ অবশ্যই</span>
      <span class="pri-tag p2">★★ গুরুত্বপূর্ণ</span>
      <span class="pri-tag p1">★ এক নজর</span>
    </div>
  </div>

  <!-- ═══ TABS ═══ -->
  <section class="main-section">
    <div class="container">

      <div class="tab-row">
        <button class="tab-btn" :class="{ active: tab === 'grammar' }" @click="tab = 'grammar'">
          <span class="tab-icon">📚</span>
          <span>বাংলা ভাষা</span>
          <span class="tab-mark">মার্ক = ১৫</span>
        </button>
        <button class="tab-btn" :class="{ active: tab === 'literature' }" @click="tab = 'literature'">
          <span class="tab-icon">📖</span>
          <span>বাংলা সাহিত্য</span>
          <span class="tab-mark">মার্ক = ১৫</span>
        </button>
      </div>

      <!-- ── GRAMMAR ── -->
      <div v-show="tab === 'grammar'" class="tab-content">

        <!-- Bar Chart -->
        <div class="chart-card">
          <h3 class="chart-title">শীর্ষ টপিক — মোট প্রশ্নসংখ্যা (বিসিএস ৩৫–৫০)</h3>
          <div class="bar-list">
            <div v-for="row in grammarChart" :key="row.topic" class="bar-row">
              <div class="bar-label">{{ row.topic }}</div>
              <div class="bar-track">
                <div class="bar-fill" :style="{ width: (row.total / 24 * 100) + '%' }" :class="'bp' + row.priority"></div>
              </div>
              <div class="bar-val">{{ toBengali(row.total) }}</div>
            </div>
          </div>
        </div>

        <!-- Heatmap Table -->
        <div class="section-head">
          <h2>টপিকভিত্তিক প্রশ্ন হিটম্যাপ</h2>
          <p>বিসিএস ৩৫ থেকে ৫০ পর্যন্ত প্রতিটি বিসিএসে কতটি প্রশ্ন এসেছে</p>
        </div>

        <div class="table-wrap">
          <table class="heat-table">
            <thead>
              <tr>
                <th class="t-topic">টপিক</th>
                <th>৩৫</th><th>৩৬</th><th>৩৭</th><th>৩৮</th><th>৩৯</th>
                <th>৪০</th><th>৪১</th><th>৪২</th><th>৪৩</th><th>৪৪</th><th>৪৫</th>
                <th class="t-new">৪৬</th><th class="t-new">৪৭</th><th class="t-new">৫০</th>
                <th class="t-total">মোট</th>
                <th class="t-pri">গুরুত্ব</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in grammarData" :key="row.topic">
                <td class="t-name">{{ row.topic }}</td>
                <td v-for="(val, i) in row.values" :key="i" :class="heatClass(val)">
                  {{ val > 0 ? toBengali(val) : '' }}
                </td>
                <td class="t-total-cell">{{ toBengali(row.total) }}</td>
                <td class="t-pri-cell"><span :class="'pb p' + row.priority">{{ priorityLabel(row.priority) }}</span></td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Insight Cards -->
        <div class="insight-grid">
          <div class="ins-card must">
            <div class="ins-head">★★★ অবশ্যই পড়তে হবে</div>
            <ul>
              <li>বানান ও বাক্যশুদ্ধি</li>
              <li>সমার্থক / প্রতিশব্দ</li>
              <li>ধ্বনি ও বর্ণ</li>
              <li>পারিভাষিক শব্দ</li>
              <li>বিপরীত শব্দ</li>
              <li>বানানের নিয়ম</li>
            </ul>
          </div>
          <div class="ins-card imp">
            <div class="ins-head">★★ গুরুত্বপূর্ণ</div>
            <ul>
              <li>বাগধারা</li>
              <li>বাক্য</li>
              <li>প্রকৃতি ও প্রত্যয়</li>
              <li>সমাস</li>
              <li>এক কথায় প্রকাশ</li>
              <li>পদ</li>
            </ul>
          </div>
          <div class="ins-card low">
            <div class="ins-head">★ এক নজর দেখুন</div>
            <ul>
              <li>সন্ধি</li>
              <li>উপসর্গ</li>
              <li>কারক ও বিভক্তি</li>
              <li>ধ্বনি পরিবর্তন</li>
              <li>প্রয়োগ-অপপ্রয়োগ</li>
              <li>ছন্দ, যতিচিহ্ন</li>
            </ul>
          </div>
        </div>

        <div class="analysis-box">
          <h4>📊 বিশ্লেষণ</h4>
            <p>বাংলা ভাষা থেকে প্রশ্ন এনালাইসিস করলে দেখা যায়, <strong>ভাষা, ধ্বনি ও বর্ণ, সন্ধি, শব্দ, পদ পরিবর্তন, সমাস, প্রকৃতি ও প্রত্যয়, বাক্য গঠন, বানান ও বাক্যশুদ্ধি, পারিভাষিক শব্দ, সমার্থক শব্দ, বিপরীত শব্দ, আধুনিক বাংলা বানানের নিয়ম, বাগধারা</strong> — এগুলো থেকে প্রতিবছরই প্রশ্ন আসে। তাই এই টপিকগুলো গভীরে গিয়ে খুব ভালো করে পড়তে হবে।</p>
            <p class="mb-0">উল্লেখ্য, <strong>ধ্বনির পরিবর্তন, দ্বিরুক্ত শব্দ, ধাতু, ব্যাকরণ, প্রয়োগ-অপপ্রয়োগ, প্রবাদ ও প্রবচন, যতিচিহ্ন, ণ-ত্ব ও ষ-ত্ব বিধান, ছন্দ</strong> — এগুলো থেকে প্রশ্ন আসে না বললেই চলে।</p>
        </div>
      </div>

      <!-- ── LITERATURE ── -->
      <div v-show="tab === 'literature'" class="tab-content">

        <!-- Bar Chart -->
        <div class="chart-card">
          <h3 class="chart-title">শীর্ষ টপিক — মোট প্রশ্নসংখ্যা (বিসিএস ৩৫–৫০)</h3>
          <div class="bar-list">
            <div v-for="row in litChart" :key="row.topic" class="bar-row">
              <div class="bar-label">{{ row.topic }}</div>
              <div class="bar-track">
                <div class="bar-fill" :style="{ width: (row.total / 41 * 100) + '%' }" :class="'bp' + row.priority"></div>
              </div>
              <div class="bar-val">{{ toBengali(row.total) }}</div>
            </div>
          </div>
        </div>

        <!-- Heatmap Table -->
        <div class="section-head">
          <h2>টপিকভিত্তিক প্রশ্ন হিটম্যাপ</h2>
          <p>বিসিএস ৩৫ থেকে ৫০ পর্যন্ত প্রতিটি বিসিএসে কতটি প্রশ্ন এসেছে</p>
        </div>

        <div class="table-wrap">
          <table class="heat-table">
            <thead>
              <tr>
                <th class="t-topic">টপিক</th>
                <th>৩৫</th><th>৩৬</th><th>৩৭</th><th>৩৮</th><th>৩৯</th>
                <th>৪০</th><th>৪১</th><th>৪২</th><th>৪৩</th><th>৪৪</th><th>৪৫</th>
                <th class="t-new">৪৬</th><th class="t-new">৪৭</th><th class="t-new">৫০</th>
                <th class="t-total">মোট</th>
                <th class="t-pri">গুরুত্ব</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in literatureData" :key="row.topic">
                <td class="t-name">{{ row.topic }}</td>
                <td v-for="(val, i) in row.values" :key="i" :class="heatClass(val)">
                  {{ val > 0 ? toBengali(val) : '' }}
                </td>
                <td class="t-total-cell">{{ toBengali(row.total) }}</td>
                <td class="t-pri-cell"><span :class="'pb p' + row.priority">{{ priorityLabel(row.priority) }}</span></td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Insight Cards -->
        <div class="insight-grid">
          <div class="ins-card must">
            <div class="ins-head">★★★ অবশ্যই পড়তে হবে</div>
            <ul>
              <li>মধ্যযুগ</li>
              <li>গ্রন্থ ও চরিত্র</li>
              <li>উপন্যাস</li>
              <li>প্রাচীনযুগ (চর্যাপদ)</li>
              <li>পত্রিকা ও সম্পাদক</li>
              <li>মুক্তিযুদ্ধভিত্তিক গ্রন্থ</li>
            </ul>
          </div>
          <div class="ins-card imp">
            <div class="ins-head">★★ গুরুত্বপূর্ণ</div>
            <ul>
              <li>কাব্য ও কাব্যগ্রন্থ</li>
              <li>নাটক</li>
              <li>পংক্তি ও উক্তি</li>
              <li>কবিতা</li>
              <li>গান</li>
              <li>আধুনিক যুগের উন্মেষপর্ব</li>
            </ul>
          </div>
          <div class="ins-card low">
            <div class="ins-head">★ এক নজর দেখুন</div>
            <ul>
              <li>প্রবন্ধ ও ভ্রমণকাহিনী</li>
              <li>উপাধি ও ছদ্মনাম</li>
              <li>রচনার শ্রেণি</li>
              <li>ছোটগল্প</li>
              <li>ভাষা আন্দোলন বিষয়ক</li>
            </ul>
          </div>
        </div>

        <div class="analysis-box">
          <h4>📊 বিশ্লেষণ</h4>
            <p>বিগত বছরের প্রশ্ন এনালাইসিস করলে দেখা যায়, <strong>বাংলা সাহিত্যের প্রাচীনযুগ, মধ্যযুগ, আধুনিক যুগের উন্মেষপর্ব, পত্রিকা ও সাময়িকী, উপন্যাস, মুক্তিযুদ্ধ বিষয়ক চলচিত্র ও গ্রন্থ, পঙক্তি ও উক্তি</strong> — এগুলো থেকে প্রতিবছরই প্রশ্ন আসে। মধ্যযুগ থেকে সবচেয়ে বেশি প্রশ্ন এসেছে — ৪৬তম বিসিএসে ৫টি, ৪৭তমে ৪টি।</p>
            <p class="mb-0">বিশেষ করে <strong>PSC নির্ধারিত ১১ জন সাহিত্যিক এবং বাংলা সাহিত্যের পঞ্চপান্ডব</strong> খুব ভালো করে পড়তে হবে। বাকি লেখকদের শুধুমাত্র গুরুত্বপূর্ণ তথ্যগুলো পড়ুন — সব পড়ার দরকার নেই।</p>
        </div>

        <!-- ══ 36 Authors Section ══ -->
        <div class="authors-section">
          <div class="section-head">
            <h2>গুরুত্বপূর্ণ ৩৬ জন সাহিত্যিক</h2>
            <p>PSC নির্ধারিত ১১ জন, পঞ্চপান্ডব ৫ জন এবং অতিরিক্ত গুরুত্বপূর্ণ ২০ জন লেখক</p>
          </div>

          <div class="author-groups">
            <!-- PSC 11 -->
            <div class="author-group">
              <div class="ag-header ag-psc">
                <span class="ag-badge">PSC নির্ধারিত</span>
                <span class="ag-title">১১ জন প্রধান সাহিত্যিক</span>
                <span class="ag-note">সবচেয়ে বেশি প্রশ্ন আসে এঁদের নিয়ে</span>
              </div>
              <div class="author-grid">
                <div class="author-card ac-psc" v-for="a in pscAuthors" :key="a">
                  <span class="ac-dot psc-dot"></span>{{ a }}
                </div>
              </div>
            </div>

            <!-- Panchaপান্ডব 5 -->
            <div class="author-group">
              <div class="ag-header ag-pancha">
                <span class="ag-badge">আধুনিক কবিতা</span>
                <span class="ag-title">পঞ্চপান্ডব — ৫ জন কবি</span>
                <span class="ag-note">৩০-এর দশকের আধুনিক বাংলা কবিতার পথিকৃৎ</span>
              </div>
              <div class="author-grid">
                <div class="author-card ac-pancha" v-for="a in panchaAuthors" :key="a">
                  <span class="ac-dot pancha-dot"></span>{{ a }}
                </div>
              </div>
            </div>

            <!-- Additional 20 -->
            <div class="author-group">
              <div class="ag-header ag-extra">
                <span class="ag-badge">অতিরিক্ত গুরুত্বপূর্ণ</span>
                <span class="ag-title">২০ জন উল্লেখযোগ্য সাহিত্যিক</span>
                <span class="ag-note">এঁদের মূল তথ্য ও উল্লেখযোগ্য রচনাগুলো পড়তে হবে</span>
              </div>
              <div class="author-grid">
                <div class="author-card ac-extra" v-for="a in extraAuthors" :key="a">
                  <span class="ac-dot extra-dot"></span>{{ a }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  </section>

  <!-- ═══ FREE PDF OPT-IN ═══ -->
  <GuidelineOptin subject="bangla" pdf="https://www.bcsprostuti.com/api5/storage/pdfs/BCS-Bangla-Guideline-3_2026_06_20_14_23_56.pdf" />

  <!-- ═══ REFERENCE BOOKS ═══ -->
  <section class="ref-section">
    <div class="container">
      <div class="section-head center">
        <h2>রেফারেন্স বই</h2>
        <p>পরীক্ষায় ভালো করার জন্য এই বইগুলো অনুসরণ করুন</p>
      </div>
      <div class="book-grid">
        <div class="book-card" v-for="b in refBooks" :key="b.title">
          <div class="book-spine"></div>
          <div class="book-info">
            <div class="book-title">{{ b.title }}</div>
            <div class="book-author">{{ b.author }}</div>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- ═══ TIPS ═══ -->
  <section class="tips-section">
    <div class="container">
      <div class="section-head center">
        <h2>পরামর্শ ও কৌশল</h2>
        <p>কীভাবে বাংলায় সর্বোচ্চ নম্বর তুলবেন</p>
      </div>
      <div class="tips-grid">
        <div class="tip-card" v-for="(tip, i) in tips" :key="i">
          <div class="tip-num">{{ toBengali(i + 1) }}</div>
          <p class="tip-text">{{ tip }}</p>
        </div>
      </div>
    </div>
  </section>

  <GuidelineRelated current="bangla" />

  <!-- ═══ CTA ═══ -->
  <section class="cta-section">
    <div class="container">
      <div class="cta-grid">
        <!-- Left: text -->
        <div class="cta-left">
          <div class="cta-tag">বিসিএস প্রস্তুতি অ্যাপ</div>
          <h2 class="cta-title">বাংলায় সর্বোচ্চ নম্বর তুলুন স্মার্ট প্রস্তুতিতে</h2>
          <ul class="cta-features">
            <li><span class="cf-icon">✓</span> টপিকভিত্তিক প্রশ্ন ব্যাঙ্ক ও ব্যাখ্যা</li>
            <li><span class="cf-icon">✓</span> বিগত ৩৫–৫০ বিসিএসের সম্পূর্ণ প্রশ্নসমূহ</li>
            <li><span class="cf-icon">✓</span> স্মার্ট প্রগ্রেস ট্র্যাকিং সিস্টেম</li>
            <li><span class="cf-icon">✓</span> লাইভ কোর্স ও মডেল টেস্ট</li>
          </ul>
          <div class="cta-actions">
            <a href="https://play.google.com/store/apps/details?id=com.bcsprostuti.tanim.bcsprostuti" target="_blank" class="cta-play" @click="trackAppInstall">
              <img src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg" alt="Google Play" height="48">
            </a>
            <a href="/packages" class="cta-pkg" @click="trackInitiateCheckout">প্যাকেজ দেখুন →</a>
          </div>
          <p class="cta-count">৯ লাখ+ শিক্ষার্থীর বিশ্বাসের অ্যাপ</p>
        </div>
        <!-- Right: stat cards -->
        <div class="cta-right">
          <div class="cta-stat-card">
            <div class="cs-num">৯ লাখ+</div>
            <div class="cs-lbl">অ্যাপ ডাউনলোড</div>
          </div>
          <div class="cta-stat-card">
            <div class="cs-num">৩৫–৫০</div>
            <div class="cs-lbl">বিসিএস বিশ্লেষণ</div>
          </div>
          <div class="cta-stat-card">
            <div class="cs-num">১০০%</div>
            <div class="cs-lbl">টপিক কভারেজ</div>
          </div>
          <div class="cta-stat-card">
            <div class="cs-num">৩৬ জন</div>
            <div class="cs-lbl">গুরুত্বপূর্ণ সাহিত্যিক</div>
          </div>
        </div>
      </div>
    </div>
  </section>

  <WelcomeFooter :hide-download="true" />

  <MessengerFloat subject="bangla" />
</template>

<script>
import WelcomeHeader from "@/components/WelcomePages/WelcomeHeader.vue";
import WelcomeFooter from "@/components/WelcomePages/WelcomeFooter.vue";
import GuidelineNav from "@/components/WelcomePages/GuidelineNav.vue";
import GuidelineRelated from "@/components/WelcomePages/GuidelineRelated.vue";
import GuidelineOptin from "@/components/WelcomePages/GuidelineOptin.vue";
import MessengerFloat from "@/components/WelcomePages/MessengerFloat.vue";
import * as pixels from "@/config/pixels";
import { ref, computed, onMounted } from 'vue';

export default {
  components: { WelcomeHeader, WelcomeFooter, GuidelineNav, GuidelineRelated, GuidelineOptin, MessengerFloat },
  setup() {
    const tab = ref('grammar');

    // ── Grammar Data: BCS 35,36,37,38,39,40,41,42,43,44,45,46,47,50 ──
    const grammarData = [
      { topic: 'বানান ও বাক্যশুদ্ধি',  values: [2,1,1,2,0,1,2,2,1,2,2,2,4,2], total: 26, priority: 3 },
      { topic: 'ধ্বনি ও বর্ণ',          values: [1,2,2,2,0,0,2,1,2,1,3,2,3,1], total: 22, priority: 3 },
      { topic: 'সমার্থক/প্রতিশব্দ',    values: [2,1,1,2,2,2,0,0,0,1,1,1,2,3], total: 18, priority: 3 },
      { topic: 'পারিভাষিক শব্দ',        values: [1,0,2,1,1,1,0,2,1,1,1,1,1,1], total: 14, priority: 3 },
      { topic: 'বানানের নিয়ম',          values: [0,1,1,2,0,2,1,3,1,0,0,0,0,0], total: 11, priority: 3 },
      { topic: 'বিপরীত শব্দ',           values: [1,0,1,1,1,1,0,2,0,0,1,1,1,1], total: 11, priority: 3 },
      { topic: 'প্রকৃতি ও প্রত্যয়',    values: [1,1,0,3,0,2,1,0,0,0,1,1,0,0], total: 10, priority: 2 },
      { topic: 'সমাস',                  values: [1,1,2,1,0,0,1,0,0,1,0,1,0,2], total: 10, priority: 2 },
      { topic: 'বাগধারা',               values: [0,0,1,0,0,2,0,0,2,2,1,0,1,0], total:  9, priority: 2 },
      { topic: 'পদ',                    values: [1,1,0,0,1,0,0,0,1,0,3,0,0,2], total:  9, priority: 2 },
      { topic: 'এক কথায় প্রকাশ',       values: [0,0,0,0,0,3,1,1,1,2,0,0,0,1], total:  9, priority: 2 },
      { topic: 'বাক্য',                 values: [1,1,0,1,0,0,1,1,1,1,0,0,1,1], total:  9, priority: 2 },
      { topic: 'সন্ধি',                 values: [1,0,1,1,0,0,0,0,0,1,0,1,0,1], total:  5, priority: 1 },
      { topic: 'উপসর্গ',               values: [0,0,1,0,2,0,1,0,0,0,0,1,0,0], total:  5, priority: 1 },
      { topic: 'কারক ও বিভক্তি',       values: [0,0,0,0,1,2,0,0,1,0,0,1,0,0], total:  4, priority: 1 },
      { topic: 'প্রয়োগ-অপপ্রয়োগ',    values: [1,0,1,1,0,0,0,0,0,0,0,0,0,0], total:  3, priority: 1 },
      { topic: 'ধ্বনি পরিবর্তন',        values: [1,0,0,0,0,0,1,0,1,1,0,0,0,0], total:  3, priority: 1 },
    ];

    const literatureData = [
      { topic: 'মধ্যযুগ',                     values: [4,4,3,4,0,3,1,0,3,4,4,5,4,2], total: 41, priority: 3 },
      { topic: 'উপন্যাস',                      values: [1,2,1,1,0,2,5,0,3,3,1,2,2,2], total: 25, priority: 3 },
      { topic: 'গ্রন্থ ও চরিত্র',              values: [0,1,0,1,0,0,1,0,2,4,6,2,4,1], total: 22, priority: 3 },
      { topic: 'প্রাচীনযুগ',                   values: [2,0,2,1,0,2,2,1,2,1,1,1,1,1], total: 17, priority: 3 },
      { topic: 'পত্রিকা ও সম্পাদক',            values: [1,2,2,1,0,1,1,2,2,1,1,0,0,1], total: 15, priority: 3 },
      { topic: 'পংক্তি ও উক্তি',               values: [1,1,2,0,0,3,0,0,0,0,2,1,3,0], total: 13, priority: 2 },
      { topic: 'কাব্য ও কাব্যগ্রন্থ',           values: [0,0,1,0,0,0,2,1,2,1,1,2,2,0], total: 12, priority: 2 },
      { topic: 'মুক্তিযুদ্ধভিত্তিক গ্রন্থ',     values: [2,1,0,1,0,1,1,2,1,0,0,0,0,0], total:  9, priority: 3 },
      { topic: 'কবিতা',                        values: [1,1,1,0,0,0,0,0,2,3,0,0,1,1], total:  9, priority: 2 },
      { topic: 'নাটক',                         values: [1,2,0,0,0,0,0,0,0,1,0,2,1,1], total:  8, priority: 2 },
      { topic: 'আধুনিক যুগের উন্মেষপর্ব',      values: [0,2,3,1,0,1,0,0,0,0,0,0,1,0], total:  8, priority: 2 },
      { topic: 'গান',                          values: [1,0,0,1,0,1,0,0,0,0,0,2,0,2], total:  7, priority: 2 },
      { topic: 'প্রবন্ধ ও ভ্রমণকাহিনী',        values: [1,1,0,0,0,0,1,0,0,0,2,1,0,1], total:  7, priority: 1 },
      { topic: 'উপাধি ও ছদ্মনাম',              values: [0,0,0,1,0,0,3,0,1,0,0,0,0,1], total:  6, priority: 1 },
      { topic: 'রচনার শ্রেণি ও উপজীব্য',      values: [2,1,0,0,0,1,0,1,0,0,1,0,0,0], total:  6, priority: 1 },
      { topic: 'ছোটগল্প',                      values: [1,0,0,0,0,0,1,0,0,1,0,1,0,0], total:  4, priority: 1 },
      { topic: 'ভাষা আন্দোলন বিষয়ক',          values: [0,0,1,0,0,0,1,0,0,1,0,0,0,0], total:  3, priority: 1 },
    ];

    const grammarChart = computed(() =>
      [...grammarData].sort((a, b) => b.total - a.total).slice(0, 9)
    );
    const litChart = computed(() =>
      [...literatureData].sort((a, b) => b.total - a.total).slice(0, 9)
    );

    const pscAuthors = [
      'রবীন্দ্রনাথ ঠাকুর', 'কাজী নজরুল ইসলাম', 'ঈশ্বরচন্দ্র বিদ্যাসাগর',
      'বেগম রোকেয়া', 'জসীম উদ্দীন', 'শরৎচন্দ্র চট্টোপাধ্যায়',
      'বঙ্কিমচন্দ্র চট্টোপাধ্যায়', 'মাইকেল মধুসূদন দত্ত',
      'মীর মশাররফ হোসেন', 'দীনবন্ধু মিত্র', 'কায়কোবাদ',
    ];

    const panchaAuthors = [
      'জীবনানন্দ দাশ', 'অমিয় চক্রবর্তী', 'বুদ্ধদেব বসু',
      'বিষ্ণু দে', 'সুধীন্দ্রনাথ দত্ত',
    ];

    const extraAuthors = [
      'শামসুর রহমান', 'সৈয়দ শামসুল হক', 'সৈয়দ ওয়ালীউল্লাহ',
      'সুফিয়া কামাল', 'হাসান আজিজুল হক', 'আখতারুজ্জামান ইলিয়াস',
      'বিভূতিভূষণ বন্দ্যোপাধ্যায়', 'বিহারীলাল চক্রবর্তী',
      'মুনীর চৌধুরী', 'আল মাহমুদ', 'হেলাল হাফিজ',
      'শহীদুল জহির', 'ফররুখ আহমদ', 'শওকত ওসমান',
      'আলাউদ্দিন আল আজাদ', 'প্রমথ চৌধুরী',
      'প্যারীচাঁদ মিত্র', 'আবুল ফজল',
      'হুমায়ূন আজাদ', 'সৈয়দ মুজতবা আলী',
    ];

    const refBooks = [
      { title: 'বাংলা ব্যাকরণ বোর্ড বই', author: '৯ম-১০ম শ্রেণি' },
      { title: 'বাংলা ভাষা ও সাহিত্য জিজ্ঞাসা', author: 'সৌমিত্র শেখর' },
      { title: 'লাল নীল দীপাবলি', author: 'হুমায়ূন আজাদ' },
      { title: 'শীকর বাংলা ভাষা ও সাহিত্য', author: 'মোহসীনা নাজিলা' },
      { title: 'বাংলা একাডেমি অভিধান', author: 'বাংলা একাডেমি' },
      { title: 'ভাষা-শিক্ষা', author: 'ড. হায়াত মামুদ' },
    ];

    const tips = [
      'প্রথমেই বিসিএস প্রস্তুতি অ্যাপ থেকে বাংলা বিষয়ের বিগত বছরের প্রশ্নগুলো ব্যাখ্যাসহ পড়ে ফেলুন — কোন ধরনের প্রশ্ন আসে সে সম্পর্কে একটা সামগ্রিক ধারণা তৈরি হবে।',
      'হিটম্যাপ দেখে ★★★ টপিকগুলো আগে শেষ করুন। বাংলা ভাষার জন্য ৯-১০ বোর্ড বইটি টপিক ধরে ধরে একবারে শেষ করুন।',
      'বাংলা সাহিত্যে PSC-এর ১১ জন সাহিত্যিক ও পঞ্চপান্ডবের সকল তথ্য এবং অতিরিক্ত ২০ জনের মূল তথ্য পড়ুন।',
      'পড়া শেষ হলে বিসিএস প্রস্তুতি অ্যাপ থেকে বিষয়ভিত্তিক পরীক্ষা দিয়ে দুর্বলতা চিহ্নিত করুন এবং সেই টপিকগুলো আবার পড়ুন।',
      'অ্যাপের স্মার্ট সিস্টেম আপনার প্রগ্রেস ট্র্যাক করে। গুরুত্বপূর্ণ প্রশ্ন মার্ক করে রাখুন এবং নিয়মিত রিভিশন দিন।',
      '৬ মাস বা ১২০ দিনের লাইভ কোর্স ফলো করুন। ফাইনাল পরীক্ষার আগে ক্র্যাশ কোর্স ও চূড়ান্ত মডেল টেস্টগুলো অবশ্যই দিন।',
    ];

    // ── SEO helper: upsert a <meta> tag ──
    const setMeta = (attrs) => {
      const selector = attrs.property
        ? `meta[property="${attrs.property}"]`
        : `meta[name="${attrs.name}"]`;
      let el = document.querySelector(selector);
      if (!el) { el = document.createElement('meta'); document.head.appendChild(el); }
      Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
    };

    // ── Funnel events (centralized in @/config/pixels) ──
    const trackAppInstall = () => pixels.trackAppInstallClick('bangla');
    const trackInitiateCheckout = () => pixels.trackInitiateCheckout('bangla');

    const toBengali = (n) => {
      const bn = ['০','১','২','৩','৪','৫','৬','৭','৮','৯'];
      return String(n).split('').map(c => bn[parseInt(c)] ?? c).join('');
    };

    const heatClass = (val) => {
      if (!val || val === 0) return 'h0';
      if (val === 1) return 'h1';
      if (val === 2) return 'h2';
      return 'h3';
    };

    const priorityLabel = (p) => ({ 3: '★★★', 2: '★★', 1: '★' }[p]);

    onMounted(() => {
      window.scrollTo(0, 0);

      // ── SEO: title + meta tags ──
      document.title = 'বিসিএস বাংলা গাইডলাইন — টপিকভিত্তিক প্রশ্ন বিশ্লেষণ (৩৫–৫০ বিসিএস)';
      setMeta({ name: 'description',        content: 'বিসিএস ৩৫ থেকে ৫০ পর্যন্ত বাংলা ভাষা ও সাহিত্যের সম্পূর্ণ টপিকভিত্তিক প্রশ্ন বিশ্লেষণ। হিটম্যাপ, গুরুত্বপূর্ণ টপিক ও ৩৬ জন সাহিত্যিকের গাইডলাইন।' });
      setMeta({ name: 'keywords',           content: 'বিসিএস বাংলা গাইডলাইন, BCS Bangla, বিসিএস প্রস্তুতি, বাংলা সাহিত্য, বাংলা ব্যাকরণ, বিসিএস প্রশ্ন বিশ্লেষণ' });
      setMeta({ name: 'robots',             content: 'index, follow' });
      setMeta({ property: 'og:title',       content: 'বিসিএস বাংলা গাইডলাইন — টপিকভিত্তিক প্রশ্ন বিশ্লেষণ' });
      setMeta({ property: 'og:description', content: 'বিসিএস ৩৫–৫০ বাংলা বিষয়ের সম্পূর্ণ হিটম্যাপ বিশ্লেষণ। কোন টপিক থেকে কতটি প্রশ্ন আসে জানুন।' });
      setMeta({ property: 'og:type',        content: 'article' });
      setMeta({ property: 'og:url',         content: 'https://www.bcsprostuti.com/bangla-guideline' });
      setMeta({ property: 'og:image',        content: 'https://www.bcsprostuti.com/og/bangla-guideline.jpg' });
      setMeta({ property: 'og:image:width',  content: '1200' });
      setMeta({ property: 'og:image:height', content: '630' });
      setMeta({ property: 'og:image:alt',    content: 'বিসিএস বাংলা গাইডলাইন' });
      setMeta({ property: 'og:site_name',    content: 'বিসিএস প্রস্তুতি' });
      setMeta({ name: 'twitter:card',        content: 'summary_large_image' });
      setMeta({ name: 'twitter:title',       content: 'বিসিএস বাংলা গাইডলাইন — টপিকভিত্তিক প্রশ্ন বিশ্লেষণ' });
      setMeta({ name: 'twitter:description', content: 'বিসিএস ৩৫–৫০ বাংলা বিষয়ের সম্পূর্ণ হিটম্যাপ বিশ্লেষণ।' });
      setMeta({ name: 'twitter:image',       content: 'https://www.bcsprostuti.com/og/bangla-guideline.jpg' });

      // ── SEO: canonical link ──
      let canonical = document.querySelector('link[rel="canonical"]');
      if (!canonical) { canonical = document.createElement('link'); canonical.rel = 'canonical'; document.head.appendChild(canonical); }
      canonical.href = 'https://www.bcsprostuti.com/bangla-guideline';

      // ── Funnel: ViewContent + arm the 50%/30s engaged trigger ──
      pixels.trackGuidelineView('bangla');
      pixels.armViewedGuideline50('bangla');

      // ── JSON-LD Structured Data for Google ──
      const structuredData = {
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': 'Article',
            headline: 'বিসিএস বাংলা গাইডলাইন — টপিকভিত্তিক প্রশ্ন বিশ্লেষণ (৩৫–৫০ বিসিএস)',
            description: 'বিসিএস ৩৫ থেকে ৫০ পর্যন্ত বাংলা ভাষা ও সাহিত্যের সম্পূর্ণ টপিকভিত্তিক প্রশ্ন বিশ্লেষণ। হিটম্যাপ, গুরুত্বপূর্ণ টপিক ও ৩৬ জন সাহিত্যিকের গাইডলাইন।',
            url: 'https://www.bcsprostuti.com/bangla-guideline',
            image: 'https://www.bcsprostuti.com/img/bangla-guideline-og.png',
            author: { '@type': 'Organization', name: 'বিসিএস প্রস্তুতি', url: 'https://www.bcsprostuti.com' },
            publisher: {
              '@type': 'Organization',
              name: 'বিসিএস প্রস্তুতি',
              logo: { '@type': 'ImageObject', url: 'https://www.bcsprostuti.com/img/logo.png' }
            },
            datePublished: '2024-01-01',
            dateModified: new Date().toISOString().split('T')[0],
            inLanguage: 'bn',
            keywords: 'বিসিএস বাংলা, BCS Bangla, বিসিএস প্রস্তুতি, বাংলা সাহিত্য, বাংলা ব্যাকরণ',
          },
          {
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'হোম', item: 'https://www.bcsprostuti.com' },
              { '@type': 'ListItem', position: 2, name: 'বিসিএস বাংলা গাইডলাইন', item: 'https://www.bcsprostuti.com/bangla-guideline' },
            ]
          },
          {
            '@type': 'WebPage',
            name: 'বিসিএস বাংলা গাইডলাইন',
            url: 'https://www.bcsprostuti.com/bangla-guideline',
            description: 'বিসিএস বাংলা বিষয়ের টপিকভিত্তিক প্রশ্ন বিশ্লেষণ ও গাইডলাইন',
            isPartOf: { '@type': 'WebSite', name: 'বিসিএস প্রস্তুতি', url: 'https://www.bcsprostuti.com' },
          }
        ]
      };

      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = 'bangla-guideline-ld';
      script.textContent = JSON.stringify(structuredData);
      document.head.appendChild(script);
    });

    return {
      tab, grammarData, literatureData, grammarChart, litChart,
      pscAuthors, panchaAuthors, extraAuthors, refBooks, tips,
      toBengali, heatClass, priorityLabel,
      trackAppInstall, trackInitiateCheckout,
    };
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Noto+Serif+Bengali:wght@400;600;700;900&display=swap');

* { box-sizing: border-box; }

/* ── CSS Variables ── */
:root {
  --navy:   #191B47;
  --navy2:  #0f1130;
  --indigo: #312e81;
  --gold:   #0095FF;
  --gold2:  #007AD6;
  --red:    #DC2626;
  --red-lt: #fef2f2;
  --amber-lt: #fffbeb;
  --green-lt: #f0fdf4;
  --slate:  #64748b;
}

/* ══ HERO ══ */
.hero-section {
  position: relative;
  background: linear-gradient(140deg, #191B47 0%, #0d0f2e 55%, #1a0828 100%);
  padding: 80px 0 60px;
  overflow: hidden;
}
.hero-noise {
  position: absolute; inset: 0;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.04'/%3E%3C/svg%3E");
  pointer-events: none;
}
.hero-inner { position: relative; text-align: center; }

.hero-badge {
  display: inline-block;
  background: rgba(0,149,255,0.15);
  border: 1px solid rgba(0,149,255,0.4);
  color: #0095FF;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.8rem; font-weight: 600;
  padding: 6px 18px; border-radius: 999px;
  letter-spacing: 0.05em; margin-bottom: 20px;
}

.hero-title {
  font-family: 'Noto Serif Bengali', serif;
  font-size: clamp(2rem, 5vw, 3.2rem);
  font-weight: 900; color: #fff;
  line-height: 1.25; margin-bottom: 16px;
}
.hero-accent {
  background: linear-gradient(90deg, #0095FF, #00c6ff);
  -webkit-background-clip: text; -webkit-text-fill-color: transparent;
}
.hero-sub {
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 1.05rem; color: #94a3b8; margin-bottom: 40px;
}

.hero-stats {
  display: flex; align-items: center; gap: 0;
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.12);
  border-radius: 16px; padding: 16px 20px;
  backdrop-filter: blur(12px); flex-wrap: wrap; justify-content: center;
  gap: 0; row-gap: 12px;
}
.hstat { text-align: center; padding: 0 16px; }
.hstat-num {
  font-family: 'Noto Serif Bengali', serif;
  font-size: clamp(1.3rem, 3vw, 2rem); font-weight: 900; color: #0095FF; line-height: 1;
}
.hstat-lbl {
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.72rem; color: #94a3b8; margin-top: 4px;
}
.hstat-sep {
  width: 1px; height: 40px; background: rgba(255,255,255,0.15);
  flex-shrink: 0;
}
@media(max-width: 768px) { .hstat-sep { display: none; } .hstat { padding: 4px 12px; } }

/* ══ LEGEND BAR ══ */
.legend-bar {
  background: #f8fafc; border-bottom: 1px solid #e2e8f0;
  padding: 10px 16px; overflow-x: auto;
}
.legend-inner {
  display: flex; align-items: center; justify-content: flex-start; gap: 8px;
  font-family: system-ui, -apple-system, sans-serif; font-size: 0.82rem;
  white-space: nowrap; min-width: max-content;
}
@media(min-width: 769px) {
  .legend-inner {
    justify-content: center;
    margin: 0 auto;
  }
}
.legend-label { font-weight: 700; color: #374151; margin-right: 4px; }
.lc {
  display: inline-flex; align-items: center; justify-content: center;
  width: 30px; height: 26px; border-radius: 6px;
  font-size: 0.78rem; font-weight: 700; border: 1px solid #e2e8f0;
}
.h0, .heat-none { background: #f1f5f9; color: #94a3b8; }
.h1, .heat-low  { background: #fef9c3; color: #854d0e; }
.h2, .heat-mid  { background: #fed7aa; color: #7c2d12; }
.h3, .heat-high { background: #fca5a5; color: #7f1d1d; font-weight: 800; }
.legend-sep { color: #cbd5e1; margin: 0 4px; }
.pri-tag {
  font-size: 0.76rem; font-weight: 700; padding: 3px 10px;
  border-radius: 999px; cursor: default;
}
.pri-tag.p3 { background: #fef2f2; color: #991b1b; }
.pri-tag.p2 { background: #fffbeb; color: #92400e; }
.pri-tag.p1 { background: #f0fdf4; color: #166534; }

/* ══ MAIN SECTION ══ */
.main-section { padding: 40px 0; }
@media(max-width: 480px) { .main-section { padding: 24px 0; } }

/* ── Tabs ── */
.tab-row {
  display: flex; gap: 12px; justify-content: center;
  margin-bottom: 36px; flex-wrap: wrap;
}
.tab-btn {
  display: flex; flex-direction: column; align-items: center; gap: 6px;
  background: #f1f5f9; border: 2px solid #e2e8f0;
  border-radius: 14px; padding: 16px 28px;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.95rem; font-weight: 700; color: #475569;
  cursor: pointer; transition: all 0.22s;
  flex: 1; min-width: 150px; max-width: 240px;
}
.tab-btn:hover { background: #e2e8f0; border-color: #94a3b8; }
.tab-btn.active {
  background: #191B47;
  border-color: #191B47;
  color: #ffffff;
  box-shadow: 0 6px 20px rgba(25,27,71,0.35);
}
.tab-btn.active span { color: #ffffff !important; }
.tab-icon { font-size: 1.5rem; line-height: 1; }
.tab-mark {
  font-size: 0.7rem; font-weight: 700;
  background: rgba(255,255,255,0.2);
  color: #ffffff;
  padding: 2px 10px; border-radius: 999px;
}
.tab-btn:not(.active) .tab-mark {
  background: rgba(25,27,71,0.1); color: #191B47;
}
@media(max-width: 480px) {
  .tab-btn { padding: 10px 14px; min-width: 120px; font-size: 0.85rem; border-radius: 10px; }
  .tab-icon { font-size: 1.2rem; }
}

.tab-content {
  animation: fadeIn 0.2s ease;
  width: 100% !important;
  max-width: 100% !important;
  overflow: hidden !important;
}
@keyframes fadeIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }

/* ── Bar Chart ── */
.chart-card {
  background: #fff; border: 1px solid #e2e8f0;
  border-radius: 16px; padding: 28px 32px;
  margin-bottom: 40px; box-shadow: 0 2px 12px rgba(0,0,0,0.05);
}
.chart-title {
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 1rem; font-weight: 700; color: var(--navy);
  margin-bottom: 24px;
}
.bar-list { display: flex; flex-direction: column; gap: 10px; }
.bar-row { display: flex; align-items: center; gap: 10px; }
.bar-label {
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.78rem; font-weight: 600; color: #374151;
  width: 130px; flex-shrink: 0; text-align: right;
}
@media(max-width: 600px) {
  .bar-row { flex-wrap: wrap; gap: 6px 8px; }
  .bar-label { width: 100%; text-align: left; font-size: 0.8rem; font-weight: 700; flex-shrink: 0; }
  .bar-track { flex: 1; min-width: 0; }
  .bar-val { width: 28px; flex-shrink: 0; }
  .chart-card { padding: 16px 12px; }
}
.bar-track {
  flex: 1; height: 22px; background: #f1f5f9;
  border-radius: 6px; overflow: hidden;
}
.bar-fill {
  height: 100%; border-radius: 6px;
  transition: width 0.8s cubic-bezier(0.4,0,0.2,1);
}
.bp3 { background: linear-gradient(90deg, #DC2626, #f87171); }
.bp2 { background: linear-gradient(90deg, #D97706, #fbbf24); }
.bp1 { background: linear-gradient(90deg, #059669, #34d399); }
.bar-val {
  font-family: 'Noto Serif Bengali', serif;
  font-size: 0.85rem; font-weight: 700; color: var(--navy);
  width: 28px; text-align: right;
}

/* ── Section Head ── */
.section-head { margin-bottom: 20px; }
.section-head h2 {
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.95rem; font-weight: 700; color: var(--navy);
  margin: 0 0 3px 0;
  border-left: 3px solid var(--gold);
  display: block; padding: 2px 0 2px 10px;
}
.section-head p {
  font-family: system-ui, -apple-system, sans-serif;
  color: var(--slate); font-size: 0.8rem; margin: 0;
  padding-left: 13px;
}
.section-head.center { text-align: center; }
.section-head.center h2 {
  border-left: none; padding-left: 0; font-size: 1.1rem;
  border-bottom: 3px solid var(--gold); display: inline-block; padding-bottom: 4px;
}
.section-head.center p { padding-left: 0; margin-top: 8px; }

/* ── Heatmap Table ── */
.table-wrap {
  width: 100% !important;
  max-width: 100% !important;
  display: block !important;
  overflow-x: scroll !important;
  overflow-y: hidden !important;
  -webkit-overflow-scrolling: touch !important;
  border-radius: 14px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.08);
  margin-bottom: 36px;
}
.table-wrap::-webkit-scrollbar {
  height: 6px;
  display: block;
}
.table-wrap::-webkit-scrollbar-track {
  background: #f1f5f9;
}
.table-wrap::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}
.heat-table {
  width: 100% !important;
  max-width: none !important;
  min-width: 900px !important;
  border-collapse: collapse !important;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.82rem; background: #fff;
}
.heat-table thead tr { background: #1e2a6e; }
.heat-table th {
  color: #e2e8f0; padding: 11px 7px; text-align: center;
  font-weight: 700; white-space: nowrap; font-size: 0.76rem;
  border-right: 1px solid rgba(255,255,255,0.1);
}
.heat-table th.t-topic {
  text-align: left; padding-left: 16px; min-width: 165px; color: #fff;
  position: sticky; left: 0; background: #1e2a6e !important; z-index: 3;
  pointer-events: none;
  overflow: hidden; text-overflow: ellipsis; max-width: 165px;
}
@media(max-width: 768px) {
  .heat-table th.t-topic {
    min-width: 130px !important;
    max-width: 130px !important;
    padding-left: 8px;
  }
}
.heat-table th.t-new   { background: #0095FF; color: #1a1a1a; border-right: 1px solid rgba(0,0,0,0.1); }
.heat-table th.t-total { background: #1d4ed8; color: #fff; min-width: 50px; }
.heat-table th.t-pri   { background: #6d28d9; color: #fff; min-width: 80px; }
.heat-table td {
  padding: 7px 6px; text-align: center;
  border-bottom: 1px solid #f1f5f9;
  font-size: 0.8rem; font-family: system-ui, -apple-system, sans-serif;
}
.heat-table tbody tr:hover td { filter: brightness(0.96); }
.heat-table td.t-name {
  text-align: left !important; padding-left: 14px;
  font-weight: 600; color: #1e293b; white-space: nowrap;
  font-size: 0.8rem;
  position: sticky; left: 0; background: #fff !important; z-index: 2;
  box-shadow: 2px 0 5px rgba(0,0,0,0.05);
  pointer-events: none;
  overflow: hidden; text-overflow: ellipsis; max-width: 165px;
}
@media(max-width: 768px) {
  .heat-table td.t-name {
    min-width: 130px !important;
    max-width: 130px !important;
    padding-left: 8px;
    font-size: 0.75rem;
  }
}
.t-total-cell { background: #eff6ff; color: #1e40af; font-weight: 700; font-size: 0.8rem; }
.t-pri-cell   { background: #faf5ff; }

/* Priority badges in table */
.pb { display: inline-block; padding: 3px 9px; border-radius: 999px; font-size: 0.72rem; font-weight: 800; }
.pb.p3 { background: var(--red-lt); color: #991b1b; }
.pb.p2 { background: var(--amber-lt); color: #92400e; }
.pb.p1 { background: var(--green-lt); color: #166534; }

/* ── Insight Grid ── */
.insight-grid {
  display: grid; grid-template-columns: repeat(3, 1fr);
  gap: 16px; margin-bottom: 32px;
}
@media(max-width: 768px) { .insight-grid { grid-template-columns: 1fr; } }

.ins-card { border-radius: 14px; padding: 22px 20px; text-align: left; }
.ins-head { font-family: system-ui, -apple-system, sans-serif; font-weight: 800; font-size: 0.9rem; margin-bottom: 14px; text-align: left; }
.ins-card ul { padding-left: 18px; margin: 0; text-align: left; }
.ins-card li { font-family: system-ui, -apple-system, sans-serif; font-size: 0.88rem; margin-bottom: 7px; color: #374151; text-align: left; }
.ins-card.must { background: #fef2f2; border: 1px solid #fecaca; }
.ins-card.must .ins-head { color: #991b1b; }
.ins-card.imp  { background: #fffbeb; border: 1px solid #fde68a; }
.ins-card.imp  .ins-head { color: #92400e; }
.ins-card.low  { background: #f0fdf4; border: 1px solid #bbf7d0; }
.ins-card.low  .ins-head { color: #166534; }

/* ── Analysis Box ── */
.analysis-box {
  background: #f0f9ff; border-left: 4px solid var(--navy);
  border-radius: 0 14px 14px 0; padding: 20px 24px;
  margin-bottom: 48px; text-align: left;
}
.analysis-box h4 {
  font-family: 'Noto Serif Bengali', serif;
  font-size: 1rem; font-weight: 700; color: var(--navy); margin-bottom: 10px;
}
.analysis-box p {
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.9rem; color: #374151; margin-bottom: 10px; line-height: 1.7;
}
.mb-0 { margin-bottom: 0; }

/* ══ AUTHORS SECTION ══ */
.authors-section { margin-top: 8px; }
.author-groups { display: flex; flex-direction: column; gap: 28px; }
.author-group { border-radius: 18px; overflow: hidden; border: 1px solid #e2e8f0; }

.ag-header {
  padding: 18px 24px; display: flex; align-items: center; gap: 12px; flex-wrap: wrap;
}
.ag-psc    { background: linear-gradient(90deg, #191B47, #312e81); }
.ag-pancha { background: linear-gradient(90deg, #065f46, #047857); }
.ag-extra  { background: linear-gradient(90deg, #7c2d12, #9a3412); }

.ag-badge {
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.7rem; font-weight: 700;
  background: rgba(255,255,255,0.2); color: #fff;
  padding: 3px 10px; border-radius: 999px; letter-spacing: 0.04em;
}
.ag-title {
  font-family: 'Noto Serif Bengali', serif;
  font-size: 1.05rem; font-weight: 800; color: #fff;
}
.ag-note {
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.75rem; color: rgba(255,255,255,0.65);
  margin-left: auto; text-align: right;
}
@media(max-width: 600px) { .ag-note { display: none; } .ag-header { padding: 14px 16px; } }

.author-grid {
  display: grid; grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 8px; padding: 14px;
  background: #fafafa; text-align: left;
}
@media(max-width: 480px) {
  .author-grid { grid-template-columns: repeat(auto-fill, minmax(130px, 1fr)); gap: 6px; padding: 10px; }
  .author-card { font-size: 0.78rem; padding: 8px 8px; gap: 6px; }
}
.author-card {
  display: flex; align-items: center; gap: 10px;
  background: #fff; border: 1px solid #e2e8f0;
  border-radius: 10px; padding: 10px 14px;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.88rem; font-weight: 600; color: #1e293b;
  transition: box-shadow 0.15s; text-align: left;
}
.author-card:hover { box-shadow: 0 3px 10px rgba(0,0,0,0.1); }
.ac-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
.psc-dot   { background: #4f46e5; }
.pancha-dot { background: #059669; }
.extra-dot  { background: #dc2626; }

/* ══ REF SECTION ══ */
.ref-section { background: #f8fafc; padding: 48px 0; }
.book-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px; margin-top: 28px; width: 100%;
}
@media(max-width: 768px) { .book-grid { grid-template-columns: repeat(2, 1fr); } }
@media(max-width: 480px) { .book-grid { grid-template-columns: 1fr; } }
.book-card {
  display: flex; align-items: stretch; border-radius: 10px; overflow: hidden;
  border: 1px solid #e2e8f0; background: #fff;
  box-shadow: 0 2px 6px rgba(0,0,0,0.04);
  transition: transform 0.18s, box-shadow 0.18s;
}
.book-card:hover { transform: translateY(-2px); box-shadow: 0 6px 18px rgba(0,0,0,0.09); }
.book-spine { width: 5px; background: linear-gradient(180deg, #191B47, #312e81); flex-shrink: 0; }
.book-info { padding: 12px 14px; display: flex; flex-direction: column; justify-content: center; text-align: left; }
.book-title {
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.82rem; font-weight: 700; color: #1e293b; margin-bottom: 3px;
  line-height: 1.4; text-align: left;
}
.book-author {
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.74rem; color: var(--slate); text-align: left;
}

/* ══ TIPS ══ */
.tips-section { background: #fff; padding: 48px 0; }
.tips-grid {
  display: grid; grid-template-columns: repeat(2, 1fr);
  gap: 12px; margin-top: 28px;
}
@media(max-width: 600px) { .tips-grid { grid-template-columns: 1fr; } }
.tip-card {
  display: flex; gap: 16px; align-items: flex-start;
  background: #f8fafc; border: 1px solid #e2e8f0;
  border-radius: 14px; padding: 20px;
}
.tip-num {
  width: 32px; height: 32px; border-radius: 50%;
  background: #ffffff; color: #191B47;
  border: 2px solid #191B47;
  display: flex; align-items: center; justify-content: center;
  font-family: system-ui, -apple-system, sans-serif;
  font-weight: 800; font-size: 0.85rem; flex-shrink: 0;
}
.tip-text {
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.9rem; color: #374151; line-height: 1.7; margin: 0; text-align: left;
}

/* ══ CTA ══ */
.cta-section {
  background: #191B47;
  border-top: 4px solid var(--gold);
  padding: 64px 0;
}
.cta-grid {
  display: grid; grid-template-columns: 1fr 1fr;
  gap: 64px; align-items: center;
}
@media(max-width: 768px) { .cta-grid { grid-template-columns: 1fr; gap: 32px; } }

.cta-left { align-self: center; }
.cta-tag {
  display: inline-block;
  font-size: 0.72rem; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase;
  color: #0095FF; background: rgba(0,149,255,0.15);
  border: 1px solid rgba(0,149,255,0.35);
  padding: 4px 14px; border-radius: 999px; margin-bottom: 14px;
}
.cta-title {
  font-family: 'Noto Serif Bengali', serif;
  font-size: 1.5rem; font-weight: 800;
  color: #ffffff; line-height: 1.4; margin-bottom: 20px;
}
.cta-features {
  list-style: none; padding: 0; margin: 0 0 24px 0;
  display: flex; flex-direction: column; gap: 9px;
}
.cta-features li {
  display: flex; align-items: center; gap: 10px;
  font-size: 0.88rem; color: #c7d2fe; font-family: system-ui, -apple-system, sans-serif;
}
.cf-icon {
  width: 20px; height: 20px; background: rgba(0,149,255,0.2);
  color: #0095FF; border-radius: 50%;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 0.7rem; font-weight: 800; flex-shrink: 0;
}
.cta-actions {
  display: flex; align-items: center; gap: 12px; flex-wrap: wrap; margin-bottom: 16px;
}
.cta-play {
  display: inline-flex; align-items: center;
  background: #fff; border-radius: 10px; padding: 7px 16px;
  text-decoration: none; transition: transform 0.2s;
}
.cta-play:hover { transform: scale(1.03); }
.cta-pkg {
  display: inline-flex; align-items: center;
  background: var(--gold); color: #1a1a1a;
  font-weight: 700; font-size: 0.9rem;
  border-radius: 10px; padding: 11px 24px;
  text-decoration: none; transition: all 0.2s;
}
.cta-pkg:hover { background: var(--gold2); transform: scale(1.02); }
.cta-count { font-size: 0.78rem; color: #64748b; margin: 0; }

.cta-right {
  display: grid; grid-template-columns: 1fr 1fr;
  gap: 14px; align-self: center;
}
@media(max-width: 768px) { .cta-right { grid-template-columns: repeat(2, 1fr); gap: 10px; } }
.cta-stat-card {
  background: rgba(255,255,255,0.07);
  border: 1px solid rgba(255,255,255,0.14);
  border-radius: 14px; padding: 24px 20px; text-align: center;
}
.cs-num {
  font-family: 'Noto Serif Bengali', serif;
  font-size: 1.6rem; font-weight: 800; color: #0095FF; line-height: 1.1;
}
.cs-lbl {
  font-size: 0.75rem; color: #94a3b8; margin-top: 7px;
  font-family: system-ui, -apple-system, sans-serif;
}

/* ═══ PRINT STYLES ═══ */
@media print {
  /* Hide website navigation headers and footers */
  #navbar_top,
  .navbar,
  footer,
  .WelcomeFooter,
  .tab-row {
    display: none !important;
  }

  /* Force display both tabs */
  .tab-content {
    display: block !important;
    opacity: 1 !important;
    transform: none !important;
    page-break-inside: auto;
  }

  /* Adjust section headers for print */
  .section-head {
    margin-top: 30px;
    page-break-after: avoid;
  }

  /* Heatmap table styling for print */
  .table-wrap {
    box-shadow: none !important;
    margin-bottom: 24px;
    overflow: visible !important;
  }

  .heat-table {
    min-width: 100% !important;
    border: 1px solid #cbd5e1 !important;
  }

  .heat-table th, .heat-table td {
    border: 1px solid #cbd5e1 !important;
    padding: 6px 4px !important;
    font-size: 0.75rem !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  /* Ensure colors print properly */
  .h0, .heat-none { background: #f1f5f9 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .h1, .heat-low  { background: #fef9c3 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .h2, .heat-mid  { background: #fed7aa !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .h3, .heat-high { background: #fca5a5 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  
  .t-total-cell { background: #eff6ff !important; color: #1e40af !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .t-pri-cell   { background: #faf5ff !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .pb.p3 { background: #fef2f2 !important; color: #991b1b !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .pb.p2 { background: #fffbeb !important; color: #92400e !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .pb.p1 { background: #f0fdf4 !important; color: #166534 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }

  /* Charts in print */
  .chart-card {
    box-shadow: none !important;
    border: 1px solid #cbd5e1 !important;
    margin-bottom: 24px;
    page-break-inside: avoid;
  }

  .bar-fill {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .bp3 { background: #DC2626 !important; }
  .bp2 { background: #D97706 !important; }
  .bp1 { background: #059669 !important; }

  /* Insight Cards */
  .insight-grid {
    grid-template-columns: repeat(3, 1fr) !important;
    gap: 12px !important;
    page-break-inside: avoid;
  }

  .ins-card {
    padding: 12px !important;
    border: 1px solid #cbd5e1 !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  /* Author Section */
  .authors-section {
    page-break-inside: avoid;
  }

  .author-groups {
    gap: 15px !important;
  }

  .author-group {
    border: 1px solid #cbd5e1 !important;
  }

  .ag-header {
    padding: 10px 15px !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .ag-psc    { background: #191B47 !important; }
  .ag-pancha { background: #065f46 !important; }
  .ag-extra  { background: #7c2d12 !important; }

  .author-grid {
    grid-template-columns: repeat(auto-fill, minmax(130px, 1fr)) !important;
    padding: 10px !important;
    background: #fff !important;
  }

  .author-card {
    border: 1px solid #cbd5e1 !important;
    padding: 6px 10px !important;
    font-size: 0.8rem !important;
  }

  .ac-dot {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  
  .psc-dot   { background: #4f46e5 !important; }
  .pancha-dot { background: #059669 !important; }
  .extra-dot  { background: #dc2626 !important; }
}
</style>
